import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Table, Button, Spinner, Modal } from "react-bootstrap";
import Layout from "../../../layouts/default";
import { getTodayCampaigns, updateCampaignStatus, terminateCampaign as terminateCampaignThunk } from "../../../store/slices/campaigns/campaigns";
import RunReportFilterForm from "../EmailCampaignRunReport/RunReportFilterForm";
import { getCampaignSuggestions } from "../../../store/slices/campaigns/campaign";
import { useNavigate } from "react-router-dom";
import { ExclamationTriangle, Eye, Trash, SlashCircle, FileArrowDown } from "react-bootstrap-icons";
import { DateTime } from "luxon";
import SmartPagination from "../EmailCampaignRunReport/DynamicSmartPagination";
import JsonModal from "../EmailCampaignRunReport/JsonModel";
import { toast } from "react-toastify";
import { hasPermission } from "../../../services/utils";
import { PERMISSIONS } from "../../../contants/permission";
import { downloadCampaignContactsCsv } from "../../../services/exportFile/downloadCampaignContacts";
import { downloadIvrSyncChangeFile } from "../../../services/exportFile/downloadIvrSyncChangeFile";

const DailyIVRCampaignRunReport = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [filters, setFilters] = useState({});
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [showModel, setShowModel] = useState(false);
    const [selectedPayload, setSelectedPayload] = useState(null);
    const recordsPerPage = 10;
    const { todayCampaigns } = useSelector((store) => store.campaignsSlice);
    const currentPageData = todayCampaigns?.data?.result || [];
    const totalCount = todayCampaigns?.data?.totalCount || 0;
    const totalPages = Math.ceil(totalCount / recordsPerPage);
    const [openUpdateModal, setOpenUpdateModal] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState("");
    const [reason, setReason] = useState("");
    const [selectedCampaignId, setSelectedCampaignId] = useState(null);
    // modal + selected-run + in-progress state
    const [openTerminateModal, setOpenTerminateModal] = useState(false);
    const [terminateCampaign, setTerminateCampaign] = useState(null);
    const [terminating, setTerminating] = useState(false);
    // [CAMPAIGN_CONTACTS_EXPORT] id of the run whose contacts CSV is currently being generated
    const [downloadingId, setDownloadingId] = useState(null);
    // [IVR_SYNC] which run + which change-file (added/removed) is currently downloading
    const [syncDownloading, setSyncDownloading] = useState({ id: null, type: null });

    // [CAMPAIGN_CONTACTS_EXPORT] export this IVR run's contacted mobile numbers as CSV
    const handleDownloadContacts = async (log) => {
        try {
            setDownloadingId(log?._id);
            await downloadCampaignContactsCsv({
                dispatch,
                channel: "ivr",
                campaignId: log?.campaignId,
                campaignUuid: log?.campaignUuid,
            });
        } catch (err) {
            console.log("Error in handleDownloadContacts:", err);
            toast.error(err?.message || "Failed to download contacts CSV.");
        } finally {
            setDownloadingId(null);
        }
    };

    // [IVR_SYNC] export the Added / Removed change file (with Job Id column) for a
    // FAILED sync run, so the admin can correct the jobs manually.
    const handleDownloadSyncChanges = async (log, kind) => {
        const exportType =
            kind === "added" ? "IVR_SYNC_ADDED_EXPORT" : "IVR_SYNC_REMOVED_EXPORT";
        try {
            setSyncDownloading({ id: log?._id, type: kind });
            await downloadIvrSyncChangeFile({
                dispatch,
                exportType,
                campaignId: log?.campaignId,
                campaignUuid: log?.campaignUuid,
                runId: log?.syncRunId, // optional — backend falls back to latest run
                fileLabel: kind === "added" ? "added_contacts" : "removed_contacts",
            });
        } catch (err) {
            console.log("Error in handleDownloadSyncChanges:", err);
            toast.error(err?.message || `Failed to download ${kind} contacts file.`);
        } finally {
            setSyncDownloading({ id: null, type: null });
        }
    };


    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            await dispatch(
                getTodayCampaigns({
                    filterData: {
                        ...filters,
                        channel: filters.campaignType || 'ivr_and_ivr_marketing',
                        limit: recordsPerPage,
                        page,
                    },
                })
            );
            setLoading(false);
        };

        fetchData();
    }, [dispatch, filters, page]);

    useEffect(() => {
        setPage(1);
    }, [filters]);

    const loadCampaignOptions = async (inputValue) => {
        if (inputValue.length < 4) return [];

        try {
            const response = await dispatch(
                getCampaignSuggestions({ search: inputValue })
            ).unwrap();

            if (!Array.isArray(response)) return [];

            return response
                .filter(camp =>
                    camp?.campaignName?.toLowerCase().includes(inputValue.toLowerCase()) &&
                    camp?.channelName === 'ivr'
                )
                .map(item => ({
                    label: item.campaignName,
                    value: item.campaignId,
                }));

        } catch (error) {
            console.error("Error loading campaign options:", error);
            return [];
        }
    };

    const handleViewCampaign = (campaignUuid, status) => {
        if (!campaignUuid) {
            alert(`Campaign is ${status}`);
        } else {
            navigate(`${campaignUuid}`);
        }
    };

    const showFailedJson = (payload) => {
        setSelectedPayload(payload);
        setShowModel(true);
    };

    const closeModal = () => {
        setSelectedPayload(null);
        setShowModel(false);
    };


    const handleMarkDelete = async (data) => {
        if (!window.confirm("Are You sure you want to delete this campaign from here.")) {
            return;
        }

        const id = data._id;
        const user = JSON.parse(localStorage.getItem("user"));
        const userId = user?.data?.id;

        if (!id || !userId) {
            toast.error("Something went wrong. id or userId is missing...");
            return;
        }

        const payload = { id, userId, status: 'deleted' };
        try {
            await dispatch(updateCampaignStatus(payload)).unwrap();
            await dispatch(
                getTodayCampaigns({
                    filterData: {
                        ...filters,
                        channel: 'ivr',
                        limit: recordsPerPage,
                        page,
                    },
                })
            );

        } catch (err) {
            console.error(err);
            toast.error("Failed to delete campaign");
        }
    };

    // confirm -> call terminate API (shows a
    // loading spinner while it stops the run & prepares the CSV) -> auto-download
    // the returned complete-data CSV -> refresh the list so status shows "terminated".
    const handleTerminate = async () => {
        // userId comes from the logged-in user (stamped on the run as who terminated it)
        const user = JSON.parse(localStorage.getItem("user"));
        const userId = user?.data?.id;

        // guard: need the run's _id + campaignId + a logged-in user to proceed
        if (!terminateCampaign?._id || !terminateCampaign?.campaignId || !userId) {
            toast.error("Something went wrong. Missing campaign details.");
            return;
        }

        setTerminating(true); // locks the modal + shows spinner while the API runs
        try {
            // .unwrap() -> throws on failure (caught below), returns { csv, fileName } on success
            const data = await dispatch(
                terminateCampaignThunk({
                    id: terminateCampaign._id,
                    campaignId: terminateCampaign.campaignId,
                    userId,
                })
            ).unwrap();

            // Auto-download the CSV returned by the backend (build a Blob -> click a temp link).
            if (data?.csv) {
                const blob = new Blob([data.csv], { type: "text/csv;charset=utf-8;" });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download =
                    data.fileName || `campaign_${terminateCampaign.campaignId}_terminated.csv`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url); // free the object URL after download
            }

            // close + clear selection only after a successful terminate
            setOpenTerminateModal(false);
            setTerminateCampaign(null);

            // Refresh the list so the status reflects "terminated".
            await dispatch(
                getTodayCampaigns({
                    filterData: {
                        ...filters,
                        channel: filters.campaignType || 'ivr_and_ivr_marketing',
                        limit: recordsPerPage,
                        page,
                    },
                })
            );
        } catch (err) {
            console.error(err);
            // Error toast is handled inside the thunk.
        } finally {
            setTerminating(false);
        }
    };

    const handleUpdateStatus = async () => {
        if (!selectedStatus) {
            toast.error("Please select a status");
            return;
        }

        if (selectedStatus === "failed" && !reason.trim()) {
            toast.error("Please enter a reason for failure");
            return;
        }

        const user = JSON.parse(localStorage.getItem("user"));
        const userId = user?.data?.id;

        if (!userId) {
            toast.error("Something went wrong. userId is missing...");
            return;
        }

        if (!selectedCampaignId) {
            toast.error("Something went wrong. selectedCampaignId is missing...");
            return;
        }

        try {
            const payload = {
                id: selectedCampaignId,
                status: selectedStatus,
                failedReason: selectedStatus === "failed" ? reason : null,
                userId
            };

            await dispatch(updateCampaignStatus(payload)).unwrap();

            setOpenUpdateModal(false);
            setSelectedStatus("");
            setReason("");

            // Reload data
            await dispatch(
                getTodayCampaigns({
                    filterData: {
                        ...filters,
                        channel: 'ivr',
                        limit: recordsPerPage,
                        page,
                    },
                })
            );

        } catch (err) {
            console.error(err);
            toast.error("Failed to update campaign status");
        }
    };


    return (
        <Layout>
            <div>
                <h3 className="mt-3 mb-3">IVR Campaign Run Report</h3>

                <RunReportFilterForm
                    loadCampaignOptions={loadCampaignOptions}
                    setFilterData={setFilters}
                    shouldDisplayCampaignType={true}
                />

                <Table striped bordered hover>
                    <thead>
                        <tr>
                            <th>S.No.</th>
                            <th>Campaign Name</th>
                            <th>CampaignId</th>
                            <th>Channel Name</th>
                            <th>Scheduled Date Time</th>
                            <th>Status</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan="10" className="text-center py-4">
                                    <Spinner animation="border" variant="primary" role="status" />
                                    <div className="mt-2">Loading campaigns...</div>
                                </td>
                            </tr>
                        ) : currentPageData.length > 0 ? (
                            currentPageData.map((log, index) => {
                                const status = (log?.status || "").toLowerCase();
                                const statusColor =
                                    status === "pending"
                                        ? "orange"
                                        : status === "completed"
                                            ? "green"
                                            : status === "started"
                                                ? "gray"
                                                : "red";

                                return (
                                    <tr key={log.campaignUuid || index}>
                                        <td>{(page - 1) * recordsPerPage + index + 1}</td>
                                        <td>{log?.campaignName || "NA"}</td>
                                        <td>{log?.campaignId || "NA"}</td>
                                         <td>{log?.channelName || "NA"}</td>
                                        <td>
                                            {(() => {
                                                const rawDate = log?.scheduledTime;
                                                if (!rawDate) return "NA";

                                                let dt = DateTime.fromISO(rawDate);
                                                if (!dt.isValid) {
                                                    dt = DateTime.fromFormat(rawDate, "yyyy-MM-dd H:m:s");
                                                }

                                                return dt.isValid
                                                    ? dt.toFormat("yyyy-MM-dd HH:mm:ss")
                                                    : "Invalid Date";
                                            })()}
                                        </td>
                                        <td style={{ color: statusColor }}>
                                            {log?.status || "NA"}{" "}
                                            {log?.status === "failed" && (
                                                <ExclamationTriangle
                                                    onClick={() => showFailedJson(log)}
                                                    color="red"
                                                    size={15}
                                                    style={{ cursor: "pointer" }}
                                                />
                                            )}
                                        </td>
                                        <td>
                                            {
                                                hasPermission(PERMISSIONS.IVR_CAMPAIGN_REPORT.VIEW) && <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    className="me-2"
                                                    onClick={() =>
                                                        handleViewCampaign(log?.campaignUuid, log?.status)
                                                    }
                                                >
                                                    <Eye title="View Report" size={16} />
                                                </Button>
                                            }
                                            {
                                                log?.status === 'pending' && hasPermission(PERMISSIONS.IVR_CAMPAIGN_REPORT.DELETE) && <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={() => handleMarkDelete(log)}
                                                >
                                                    <Trash size={16} />
                                                </Button>
                                            }
                                            {
                                                log?.status === 'started' && <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={() => {
                                                        setOpenUpdateModal(true);
                                                        setSelectedCampaignId(log._id);
                                                    }}
                                                >
                                                    Update Status
                                                </Button>
                                            }
                                            {/* Terminate button — shown ONLY for
                                                "started" runs, next to the Update Status action */}
                                            {
                                                log?.status === 'started' && hasPermission(PERMISSIONS.IVR_CAMPAIGN_REPORT.DELETE) && (
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        className="ms-2"
                                                        title="Terminate Campaign"
                                                        onClick={() => {
                                                            setTerminateCampaign(log);
                                                            setOpenTerminateModal(true);
                                                        }}
                                                    >
                                                        <SlashCircle size={16} />
                                                    </Button>
                                                )
                                            }
                                            {/* [CAMPAIGN_CONTACTS_EXPORT] Download CSV — shown ONLY for
                                                "completed" runs; exports the contacted mobile numbers */}
                                            {
                                                log?.status === 'completed' && hasPermission(PERMISSIONS.IVR_CAMPAIGN_REPORT.DOWNLOAD) && (
                                                    <Button
                                                        variant="outline-success"
                                                        size="sm"
                                                        className="ms-2"
                                                        title="Download contacted numbers CSV"
                                                        disabled={downloadingId === log._id}
                                                        onClick={() => handleDownloadContacts(log)}
                                                    >
                                                        {downloadingId === log._id ? (
                                                            <Spinner size="sm" animation="border" />
                                                        ) : (
                                                            <FileArrowDown size={16} />
                                                        )}
                                                    </Button>
                                                )
                                            }
                                            {/* [IVR_SYNC] Failed-run recovery downloads — the contacts this
                                                run tried to ADD / REMOVE, each with its Job Id, so the admin
                                                can fix the Hudoblast jobs manually. Shown ONLY for "failed" runs. */}
                                            {
                                                log?.status === 'failed' && hasPermission(PERMISSIONS.IVR_CAMPAIGN_REPORT.DOWNLOAD) && (
                                                    <>
                                                        <Button
                                                            variant="outline-success"
                                                            size="sm"
                                                            className="ms-2"
                                                            title="Download Added Contacts File (with Job Id)"
                                                            disabled={syncDownloading.id === log._id}
                                                            onClick={() => handleDownloadSyncChanges(log, "added")}
                                                        >
                                                            {syncDownloading.id === log._id && syncDownloading.type === "added" ? (
                                                                <Spinner size="sm" animation="border" />
                                                            ) : (
                                                                <><FileArrowDown size={16} /> Added</>
                                                            )}
                                                        </Button>
                                                        <Button
                                                            variant="outline-danger"
                                                            size="sm"
                                                            className="ms-2"
                                                            title="Download Removed Contacts File (with Job Id)"
                                                            disabled={syncDownloading.id === log._id}
                                                            onClick={() => handleDownloadSyncChanges(log, "removed")}
                                                        >
                                                            {syncDownloading.id === log._id && syncDownloading.type === "removed" ? (
                                                                <Spinner size="sm" animation="border" />
                                                            ) : (
                                                                <><FileArrowDown size={16} /> Removed</>
                                                            )}
                                                        </Button>
                                                    </>
                                                )
                                            }
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="10" className="text-center py-4">
                                    No data available
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>

                {/* Update Status Modal */}
                {openUpdateModal && (
                    <Modal show={openUpdateModal} onHide={() => setOpenUpdateModal(false)} centered>
                        <Modal.Header closeButton>
                            <Modal.Title>Update Campaign Status</Modal.Title>
                        </Modal.Header>

                        <Modal.Body>
                            <div className="mb-3">
                                <label className="form-label">Select Status</label>
                                <select
                                    className="form-select"
                                    value={selectedStatus}
                                    onChange={(e) => setSelectedStatus(e.target.value)}
                                >
                                    <option value="">-- Select --</option>
                                    <option value="completed">Completed</option>
                                    <option value="failed">Failed</option>
                                </select>
                            </div>

                            {selectedStatus === "failed" && (
                                <div className="mb-3">
                                    <label className="form-label">Failure Reason</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Enter failure reason"
                                        value={reason}
                                        onChange={(e) => setReason(e.target.value)}
                                    />
                                </div>
                            )}
                        </Modal.Body>

                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setOpenUpdateModal(false)}>
                                Close
                            </Button>
                            <Button variant="primary" onClick={handleUpdateStatus}>
                                Update
                            </Button>
                        </Modal.Footer>
                    </Modal>

                )}

                {/* confirmation modal — disables buttons +
                    shows a spinner while terminating, and blocks closing mid-process */}
                {openTerminateModal && (
                    <Modal
                        show={openTerminateModal}
                        onHide={() => {
                            if (terminating) return; // don't allow closing while in progress
                            setOpenTerminateModal(false);
                            setTerminateCampaign(null);
                        }}
                        backdrop={terminating ? "static" : true}
                        keyboard={!terminating}
                        centered
                    >
                        <Modal.Header closeButton={!terminating}>
                            <Modal.Title>Terminate Campaign</Modal.Title>
                        </Modal.Header>

                        <Modal.Body>
                            <p className="mb-0">
                                Are you sure you want to terminate
                                {terminateCampaign?.campaignName ? ` "${terminateCampaign.campaignName}"` : " this campaign"}?
                                The campaign will be stopped immediately and the complete campaign data will be downloaded as a CSV.
                            </p>
                            {terminating && (
                                <p className="mb-0 mt-2 text-muted small">
                                    Stopping the campaign and preparing the CSV… this can take a few seconds, please don’t close this window.
                                </p>
                            )}
                        </Modal.Body>

                        <Modal.Footer>
                            <Button
                                variant="secondary"
                                disabled={terminating}
                                onClick={() => {
                                    setOpenTerminateModal(false);
                                    setTerminateCampaign(null);
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="danger"
                                disabled={terminating}
                                onClick={handleTerminate}
                            >
                                {terminating ? (
                                    <>
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                            className="me-2"
                                        />
                                        Terminating…
                                    </>
                                ) : (
                                    "Terminate"
                                )}
                            </Button>
                        </Modal.Footer>
                    </Modal>
                )}


                <JsonModal
                    show={showModel}
                    onClose={closeModal}
                    payload={selectedPayload}
                />

                {totalPages > 1 && (
                    <SmartPagination
                        totalPages={totalPages}
                        currentPage={page}
                        onPageChange={setPage}
                    />
                )}
            </div>
        </Layout>
    );
};

export default DailyIVRCampaignRunReport;
